import Modal from "@/common/components/Modal"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import TextArea from "@/common/components/TextArea"
import { unities } from "@/common/constants/unities.constants"
import { FormTransformers } from "@/common/utils/formTransformers"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dispatch, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import useUpdateEquipmentUsage from "../hooks/useUpdateEquipmentUsage"
import { editEquipmentUsageFormSchema, EditEquipmentUsageValidationSchema } from "../schemas/editEquipmentUsage.schema"
import { UnitTextField } from "@/common/components/UnitTextField"
import { FileUploader } from "@/common/components/FileUploader"
import { ErrorMessage } from "@/common/components/ErrorMessage"
import { useQueryClient } from "@tanstack/react-query"
import ResumeItem from "@/common/components/ResumeItem"

type EditEquipmentUsageModalProps = {
  setIsOpen: Dispatch<React.SetStateAction<boolean>>
  isOpen: boolean
  equipmentId: string
  initialValue?: number
  unitForConsumption?: string
  categoryId?: string;
  categoryName?: string;
}

const EditEquipmentUsageModal = ({ setIsOpen, isOpen, equipmentId, categoryId, categoryName, initialValue, unitForConsumption }: EditEquipmentUsageModalProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    mutate: updateEquipmentUsage,
    isPending: isUpdateEquipmentUsagePending,
  } = useUpdateEquipmentUsage({
    equipmentId,
    categoryId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] })
      queryClient.invalidateQueries({ queryKey: ['equipmentLastUsageDeclarationsPerCategory', equipmentId] });

      setIsOpen(false)
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  })

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<EditEquipmentUsageValidationSchema>({
    resolver: zodResolver(editEquipmentUsageFormSchema),
    defaultValues: {
      value: initialValue?.toString(),
      reason: "",
    }
  })

  const file = watch('file');

  const onSubmit = (form: EditEquipmentUsageValidationSchema) => updateEquipmentUsage(form);

  useEffect(reset, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Justificativa de ajuste"
        titleClass="text-[24px]"
        footer="Confirmar justificativa e salvar alteração"
        subtitle="Preencha o motivo para alteração."
        subtitleClassName="mt-4"
        setIsOpen={setIsOpen}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={isUpdateEquipmentUsagePending}
        disabled={isUpdateEquipmentUsagePending}
        body={(
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-6">
            {categoryId && categoryName && (
              <ResumeItem
                label="Categoria"
                value={categoryName}
              />
            )}

            <Controller
              control={control}
              name="value"
              disabled={isUpdateEquipmentUsagePending}
              render={({ field }) => (
                <UnitTextField
                  markAsRequired
                  className="text-white-800"
                  label="Novo Valor"
                  maxValue={99_999_999}
                  decimals={0}
                  unit={unities[unitForConsumption || 'kilometers']}
                  defaultValue={initialValue}
                  errorMessage={errors.value?.message}
                  {...field}
                />
              )}
            />

            <TextArea
              className="text-white-800"
              label="Justificativa"
              markAsRequired
              placeholder="Escreva aqui sua justificativa"
              errorMessage={errors.reason?.message}
              {...register("reason")}
              disabled={isUpdateEquipmentUsagePending}
            />

            <div>
              <FileUploader
                name="file"
                accept="application/pdf, image/png, image/jpg, image/jpeg"
                title="Anexe o documento"
                className="w-full bg-yellow-op-2 border-none text-white-400"
                fileName={file ? file.name : undefined}
                currentFileUrl={file ? URL.createObjectURL(file) : undefined}
                handleChange={(file) => {
                  clearErrors('file')
                  setValue('file', file)
                }}
                setError={(message) => setError('file', { message })}
                disabled={isUpdateEquipmentUsagePending}
              />

              {errors.file?.message && (
                <ErrorMessage
                  name="file"
                  message={errors.file?.message}
                />
              )}
            </div>
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages as string[]}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  )
}

export default EditEquipmentUsageModal
